import EmptyState from '@pinecast/common/EmptyState';
import {FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import * as React from 'react';

import SaveStatusIndicator from './SaveStatusIndicator';
import TranscriptUtteranceEditor from './TranscriptUtteranceEditor';
import TranscriptUtteranceSpeakerEditor from './TranscriptUtteranceSpeakerEditor';
import type {
  TranscriptSaveStatus,
  UtteranceData,
  UtteranceSaveStatus,
} from './types';

type Props = {
  onEditUtterance?: (
    text: string,
    index: number,
    previousText: string,
  ) => Promise<void>;
  onEditUtteranceSpeaker?: (speaker: string, index: number) => Promise<void>;
  onMergeUtterance?: (index: number) => Promise<void>;
  onSplitUtterance?: (index: number, previousText: string) => Promise<void>;
  utteranceData: UtteranceData;
  speakerMap?: Record<string, string>;
  showSpeakerLabels?: boolean;
};

// Tracks the save state reported by each utterance editor and folds it into
// one transcript-wide status for the indicator and the unload guard.
const useAggregateSaveStatus = () => {
  const statusesRef = React.useRef(new Map<string, UtteranceSaveStatus>());
  const [aggregate, setAggregate] =
    React.useState<TranscriptSaveStatus>('idle');
  const handleStatusChange = React.useCallback(
    (editorId: string, status: UtteranceSaveStatus | null) => {
      const statuses = statusesRef.current;
      if (status === null) {
        statuses.delete(editorId);
      } else {
        statuses.set(editorId, status);
      }
      let next: TranscriptSaveStatus = 'idle';
      const values = Array.from(statuses.values());
      if (values.includes('error')) {
        next = 'error';
      } else if (values.includes('saving') || values.includes('unsaved')) {
        next = 'saving';
      } else if (values.includes('saved')) {
        next = 'saved';
      }
      setAggregate(next);
    },
    [],
  );
  return {aggregate, handleStatusChange};
};

const TranscriptReader = ({
  onEditUtterance,
  onEditUtteranceSpeaker,
  onMergeUtterance,
  onSplitUtterance,
  utteranceData,
  showSpeakerLabels,
  speakerMap,
}: Props) => {
  const css = useCSS();

  const {aggregate, handleStatusChange} = useAggregateSaveStatus();
  const isEditing = Boolean(onEditUtterance);

  // Warn before the tab closes while edits are still unsaved or in flight.
  const hasUnsavedChanges = aggregate === 'saving' || aggregate === 'error';
  React.useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Show the speakers if:
  // 1. We're asked to show the speaker labels
  // 2. We have a speaker map with at least one speaker
  // 3. There are at least two speakers in the transcript
  // We don't show speakers if there is only one speaker in the transcript
  const showSpeakers =
    !!showSpeakerLabels ||
    (speakerMap && Object.keys(speakerMap).length > 0) ||
    new Set(utteranceData.map(u => u.speaker)).size > 1;

  const allSpeakerLabels = React.useMemo(
    () => Array.from(new Set(utteranceData.map(u => u.speaker))),
    [utteranceData],
  );

  return utteranceData.length ? (
    <>
      {isEditing && <SaveStatusIndicator status={aggregate} />}
      <div
        className={css({
          display: 'grid',
          columnGap: '8px',
          rowGap: '8px',
          gridTemplateColumns: showSpeakers ? 'max-content 1fr' : '1fr',
        })}
      >
        {utteranceData.map((utterance, i) => {
          // Can merge if this utterance has the same speaker as the previous one
          const canMergeWithPrevious =
            i > 0 && utterance.speaker === utteranceData[i - 1].speaker;

          return (
            <React.Fragment key={utterance.start}>
              {showSpeakers && (
                <TranscriptUtteranceSpeakerEditor
                  speaker={utterance.speaker}
                  index={i}
                  speakerMap={speakerMap}
                  allSpeakerLabels={allSpeakerLabels}
                  showSpeakerLabels={!!showSpeakerLabels}
                  onEdit={onEditUtteranceSpeaker}
                  canMergeWithPrevious={canMergeWithPrevious}
                  onMerge={
                    onMergeUtterance ? () => onMergeUtterance(i) : undefined
                  }
                />
              )}
              <TranscriptUtteranceEditor
                text={utterance.text}
                index={i}
                onEdit={onEditUtterance}
                onSplit={
                  onSplitUtterance
                    ? previousText => onSplitUtterance(i, previousText)
                    : undefined
                }
                onSaveStatusChange={isEditing ? handleStatusChange : undefined}
              />
            </React.Fragment>
          );
        })}
      </div>
    </>
  ) : (
    <EmptyState
      title={
        <FormattedMessage
          description="Title for the empty state when there is no transcript data"
          defaultMessage="No transcript data"
        />
      }
      copy={
        <FormattedMessage
          description="Copy for the empty state when there is no transcript data"
          defaultMessage="The transcript shows that there was no spoken content in the episode."
        />
      }
    />
  );
};

export default TranscriptReader;
